import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { listBlogs } from "src/actions/blogActions";
import { getAllDoctors } from "src/actions/doctorActions";
import { imgUrl } from "src/actions/types";
import { Button, Card } from "react-bootstrap";
import {
  createBlogMetric,
  updateBlogMetric,
  fetchBlogMetrics,
} from "src/actions/blogMetricsActions";
import { useParams, useHistory } from "react-router-dom";

const DoctorsBlogPage = () => {
  const dispatch = useDispatch();
  const blogDetailRef = useRef(null);
  const blogIdRef = useRef(null);
  const history = useHistory();
  const { blogId } = useParams();

  const { blogs = [], loading: blogsLoading, error: blogsError } = useSelector(
    (state) => state.blogReducer
  );
  const {
    doctors = [],
    loading: doctorsLoading,
    error: doctorsError,
  } = useSelector((state) => state.doctorList);
  const { metrics = {}, loading: metricsLoading, error: metricsError } =
    useSelector((state) => state.blogMetricsReducer);

  const [mergedBlogs, setMergedBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [dateFilter, setDateFilter] = useState("latest");

  // Blog metric states for selected blog
  const [likesCount, setLikesCount] = useState(0);
  const [dislikesCount, setDislikesCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisliked, setIsDisliked] = useState(false);
  const [metricInitialized, setMetricInitialized] = useState(false);

  // User ID from localStorage loginData
  const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
  const userId = loginData.hospitalID || null;

  
    useEffect(() => {
      fetchBlogMetrics()
    }, [blogs])
  useEffect(() => {
    console.log("[useEffect] Load blogs and doctors");
    dispatch(listBlogs());
    dispatch(getAllDoctors());
  }, [dispatch]);

  useEffect(() => {
    if (selectedBlog && blogDetailRef.current) {
      console.log("[useEffect] Scroll to blog detail");
      blogDetailRef.current.scrollIntoView({ behavior: "smooth" });
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [selectedBlog]);

  useEffect(() => {
    console.log("[useEffect] Sync selected blog with URL blogId:", blogId);
    if (blogId && mergedBlogs.length) {
      const foundBlog = mergedBlogs.find((b) => String(b.id) === String(blogId));
      if (foundBlog) {
        console.log("[useEffect] Found blog in mergedBlogs for blogId", blogId);
        setSelectedBlog(foundBlog);
      } else {
        console.log("[useEffect] BlogId not found, resetting selection");
        setSelectedBlog(null);
        history.replace("/doctors-blog");
      }
    } else if (!blogId) {
      setSelectedBlog(null);
    }
  }, [blogId, mergedBlogs, history]);

  useEffect(() => {
    console.log("[useEffect] Fetching blog metrics with userId:", userId);
    dispatch(fetchBlogMetrics());
  }, [dispatch, userId]);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      console.log("[useEffect] Search term changed, clearing selected blog");
      setSelectedBlog(null);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (blogs.length && doctors.length) {
      const merged = blogs
        .filter(blog => blog.status === "published")
        .map(blog => {
          // Identify if blog is created by admin
          const isAdminBlog = blog.hospitalID === 0;
  
          // Find doctor info only if not admin blog
          const doctor = isAdminBlog ? null : doctors.find(d => d.hospitalID === blog.hospitalID);
  
          return {
            ...blog,
            doctorName: doctor
              ? `Dr. ${doctor.firstName} ${doctor.lastName}`
              : isAdminBlog 
                ? "Admin"
                : "Unknown",
            doctorImage: doctor
              ? (doctor.uploadPhoto ? `${imgUrl}${doctor.uploadPhoto}` : "/assets/images/default-doctor-avatar.png")
              : isAdminBlog
                ? "/assets/images/admin-avatar.png"  // Provide admin avatar image here
                : "/assets/images/default-avatar.png",
            doctorMobile: doctor
              ? doctor.phoneNumber || "-"
              : isAdminBlog
                ? "admin@medisevas.com"  // Admin’s contact info if desired
                : "-",
            doctorSpeciality: doctor
              ? (doctor.Specializations?.map(s => s.Specialization).join(", ") || "N/A")
              : isAdminBlog
                ? "Admin"
                : "N/A",
          };
        });
      setMergedBlogs(merged);
    }
  }, [blogs, doctors]);
  
  useEffect(() => {
    if (!selectedBlog || !userId) return;
    const blogIdLocal = selectedBlog.id;
    if (metricInitialized) return;

    const blogMetricData = metrics[blogIdLocal];
    const currentUserMetric = blogMetricData?.currentUserMetric;

    if (!currentUserMetric || !currentUserMetric.id) {
      // User has no metric record for this blog yet — create with views=1
      dispatch(
        createBlogMetric({
          userId,
          blogId: blogIdLocal,
          views: 1,
          likes: 0,
          dislikes: 0,
          reaction: null,
        })
      );
      setViewsCount((prev) => prev + 1); // You can optimistic increment viewsCount or use 1 here
      setLikesCount(0);
      setDislikesCount(0);
      setIsLiked(false);
      setIsDisliked(false);
    } else {
      // User already viewed this blog — do NOT increment views
      setViewsCount(blogMetricData?.uniqueViewersCount || 0);
      setLikesCount(blogMetricData?.uniqueLikesCount || 0);
      setDislikesCount(blogMetricData?.uniqueDislikesCount || 0);

      const reaction = currentUserMetric.reaction;
      setIsLiked(reaction === "like");
      setIsDisliked(reaction === "dislike");
    }

    setMetricInitialized(true);
  }, [metrics, selectedBlog, userId, dispatch, metricInitialized]);

  const filteredBlogs = useMemo(() => {
    console.log("[useMemo] Filtering and sorting blogs");
    let filtered = mergedBlogs;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (blog) =>
          blog.title?.toLowerCase().includes(lower) ||
          blog.content?.toLowerCase().includes(lower) ||
          blog.doctorName?.toLowerCase().includes(lower) ||
          blog.mobile?.toLowerCase().includes(lower) ||
          blog.speciality?.toLowerCase().includes(lower)
      );
    }

    filtered = filtered.slice().sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      if (dateFilter === "latest") return dateB - dateA;
      return dateA - dateB;
    });

    return filtered;
  }, [mergedBlogs, searchTerm, dateFilter]);

  const relatedBlogs = useMemo(() => {
    if (!selectedBlog) return [];

    const selectedTitle = selectedBlog.title?.toLowerCase() || "";
    const keywords = selectedTitle.split(/\s+/).filter((k) => k.length > 2);

    return mergedBlogs
      .filter((b) => {
        if (b.id === selectedBlog.id) return false;
        const bTitle = b.title?.toLowerCase() || "";

        return keywords.some((kw) => {
          if (bTitle.includes(kw)) return true;
          const bWords = bTitle.split(/\s+/);
          return bWords.some((w) => w.includes(kw) || kw.includes(w));
        });
      })
      .slice(0, 5);
  }, [selectedBlog, mergedBlogs]);

  const doctorBlogs = useMemo(() => {
    if (!selectedBlog) return [];
    return mergedBlogs.filter(
      (b) => b.hospitalID === selectedBlog.hospitalID && b.id !== selectedBlog.id
    );
  }, [selectedBlog, mergedBlogs]);

  const onLikeClick = () => {
    if (!userId || !selectedBlog) {
      console.log("[onLikeClick] Early return, no user or blog selected");
      return;
    }

    const blogIdLocal = selectedBlog.id;
    const blogMetricData = metrics[blogIdLocal] || {};
    const currentUserMetric = blogMetricData.currentUserMetric || {};
    const metricId = currentUserMetric.id;

    let newLikes = likesCount;
    let newDislikes = dislikesCount;
    let newReaction = null;

    if (isLiked) {
      newLikes = Math.max(0, likesCount - 1);
      newReaction = null;
    } else {
      newLikes = likesCount + 1;
      if (isDisliked) {
        newDislikes = Math.max(0, dislikesCount - 1);
      }
      newReaction = "like";
    }

    console.log(
      `[onLikeClick] Updating metric ${metricId}, likes: ${newLikes}, dislikes: ${newDislikes}, reaction: ${newReaction}`
    );

    setLikesCount(newLikes);
    setDislikesCount(newDislikes);
    setIsLiked(!isLiked);
    setIsDisliked(false);

    const payload = {
      id: metricId,
      userId,
      blogId: blogIdLocal,
      views: viewsCount,
      likes: newLikes,
      dislikes: newDislikes,
      reaction: newReaction,
    };

    if (metricId) {
      dispatch(updateBlogMetric(payload));
    } else {
      dispatch(createBlogMetric(payload));
    }
  };
  useEffect(() => {
    if (selectedBlog && userId) {
      console.log(`[useEffect] Blog changed to ${selectedBlog.id}, refetching metrics`);
      dispatch(fetchBlogMetrics());
      setMetricInitialized(false); // Reset so metrics re-initialize for new blog
    }
  }, [selectedBlog, userId, dispatch]);
  
  const onDislikeClick = () => {
    if (!userId || !selectedBlog) {
      console.log("[onDislikeClick] Early return, no user or blog selected");
      return;
    }

    const blogIdLocal = selectedBlog.id;
    const blogMetricData = metrics[blogIdLocal] || {};
    const currentUserMetric = blogMetricData.currentUserMetric || {};
    const metricId = currentUserMetric.id;

    let newLikes = likesCount;
    let newDislikes = dislikesCount;
    let newReaction = null;

    if (isDisliked) {
      newDislikes = Math.max(0, dislikesCount - 1);
      newReaction = null;
    } else {
      newDislikes = dislikesCount + 1;
      if (isLiked) {
        newLikes = Math.max(0, likesCount - 1);
      }
      newReaction = "dislike";
    }

    console.log(
      `[onDislikeClick] Updating metric ${metricId}, likes: ${newLikes}, dislikes: ${newDislikes}, reaction: ${newReaction}`
    );

    setLikesCount(newLikes);
    setDislikesCount(newDislikes);
    setIsDisliked(!isDisliked);
    setIsLiked(false);

    const payload = {
      id: metricId,
      userId,
      blogId: blogIdLocal,
      views: viewsCount,
      likes: newLikes,
      dislikes: newDislikes,
      reaction: newReaction,
    };

    if (metricId) {
      dispatch(updateBlogMetric(payload));
    } else {
      dispatch(createBlogMetric(payload));
    }
  };

  const onSelectBlog = (blog) => {
    console.log("[onSelectBlog] Selected blog id:", blog.id);
    setSelectedBlog(blog);
    setMetricInitialized(false);
    history.push(`/doctors-blog/${blog.id}`);
    setTimeout(() => {
      if (blogIdRef.current) blogIdRef.current.scrollIntoView({ behavior: "smooth" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const onClearSelection = () => {
    console.log("[onClearSelection] Clearing selected blog");
    setSelectedBlog(null);
    history.push("/doctors-blog");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      <div className="breadcrumb-bar">
        <div className="container-fluid">
          <nav aria-label="breadcrumb" className="page-breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/DoctorDashboardMain">Home</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                View Blogs
              </li>
            </ol>
          </nav>
          <h6 className="breadcrumb-title">View Blogs</h6>
        </div>
      </div>
      <div className="doctors-blog-page container " >
        <Helmet>
          <title>MediSevas | Health Blog</title>
          <meta name="description" content="Expert medical articles by doctors at MediSevas." />
        </Helmet>

        <div className="container text-center mb-4">
          <h1 className="display-5 fw-bold">Health Blog</h1>
          <p className="mt-2 lead">
            Expert articles and health tips from our specialist doctors
          </p>
        </div>

        <div className="mb-4 d-flex justify-content-center align-items-center gap-3">
          <input
            type="search"
            className="form-control shadow-sm"
            style={{ maxWidth: 400 }}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search blogs"
          />
          <select
            className="form-select shadow-sm"
            style={{ maxWidth: 150 }}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            aria-label="Sort by date"
          >
            <option value="latest">Date: Latest</option>
            <option value="oldest">Date: Oldest</option>
          </select>
        </div>

        {(blogsLoading || doctorsLoading || metricsLoading) && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" aria-hidden="true" />
            <span className="visually-hidden">Loading...</span>
          </div>
        )}

        {(blogsError || doctorsError || metricsError) && (
          <div className="alert alert-danger text-center">
            {blogsError || doctorsError || metricsError}
          </div>
        )}

        {!blogsLoading && !doctorsLoading && filteredBlogs.length === 0 && (
          <div className="alert alert-info text-center">No blogs found matching your criteria.</div>
        )}

        {selectedBlog && (
          <div
            className="row gx-4 mt-5"
            ref={blogDetailRef}
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <div className="col-lg-8 col-md-7 ">
              <Card className="p-4 shadow-sm">
                <h2>{selectedBlog.title}</h2>
                {selectedBlog.image && (
                  <img
                    src={`${imgUrl}${selectedBlog.image}`}
                    alt={selectedBlog.title}
                    className="img-fluid rounded mb-3"
                  />
                )}
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedBlog.content || "<p>No content available.</p>",
                  }}
                />
                <hr />
                <p>
                  <strong>Author:</strong> {selectedBlog.doctorName}
                </p>
                <p>
                  <strong>Published:</strong> {formatDate(selectedBlog.createdAt)}
                </p>
                <hr />
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      variant={isLiked ? "success" : "outline-success"}
                      className="rounded-pill"  
                      onClick={onLikeClick}
                      aria-pressed={isLiked}
                      aria-label="Like blog"
                    >
                      👍 Like ({likesCount})
                    </Button>
                    <Button
                      variant={isDisliked ? "danger" : "outline-danger"}
                      className="rounded-pill"  
                      onClick={onDislikeClick}
                      aria-pressed={isDisliked}
                      aria-label="Dislike blog"
                    >
                      👎 Dislike ({dislikesCount})
                    </Button>
                    <div className="ms-auto text-muted" aria-label="Unique views count">
                      👁️ Views: {viewsCount}
                    </div>
                  </div>



                </div>
              </Card>
            </div>

            <div className="col-lg-4 col-md-5 mb-4">
              <div style={{ position: "sticky", top: "90px" }}>
                <Card className="mb-4 p-3 shadow-sm" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <h5>Other Blogs by {selectedBlog.doctorName}</h5>
                  {doctorBlogs.length === 0 && <div className="text-muted">No other blogs by this doctor.</div>}
                  {doctorBlogs.map((blog) => (
                    <Button
                      key={blog.id}
                      variant="link"
                      className="d-block p-1 text-start"
                      onClick={() => onSelectBlog(blog)}
                    >
                      {blog.title}
                    </Button>
                  ))}
                </Card>
                <Card className="p-3 shadow-sm" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <h5>Related Blogs</h5>
                  {relatedBlogs.length === 0 && <div className="text-muted">No related blogs found.</div>}
                  {relatedBlogs.map((blog) => (
                    <Button
                      key={blog.id}
                      variant="link"
                      className="d-block p-1 text-start"
                      onClick={() => onSelectBlog(blog)}
                    >
                      {blog.title}
                    </Button>
                  ))}
                </Card>
              </div>
            </div>
          </div>
        )}

        <div className="row gy-4">
          {filteredBlogs.map((blog) => (
            <article
              key={blog.id}
              className="col-sm-12 col-md-6 col-lg-4 mb-4"
              style={{
                cursor: "pointer",
                border: selectedBlog?.id === blog.id ? "2px solid #007bff" : "none",
                borderRadius: 8,
              }}
              onClick={() => onSelectBlog(blog)}
              aria-selected={selectedBlog?.id === blog.id}
            >
              <div className="card h-100 shadow border-0 rounded-3">
                {blog.image ? (
                  <img
                    src={`${imgUrl}${blog.image}`}
                    alt={blog.title}
                    className="card-img-top rounded-top"
                    style={{
                      height: 200,
                      objectFit: "cover",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                ) : (
                  <div
                    className="bg-secondary d-flex align-items-center justify-content-center rounded-top"
                    style={{ height: 200 }}
                  >
                    <i className="fas fa-image fa-4x text-white" />
                  </div>
                )}
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title">{blog.title}</h5>
                  <p
                    className="card-text flex-grow-1 text-muted"
                    style={{ maxHeight: 72, overflow: "hidden" }}
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />
                  <div className="d-flex align-items-center mt-0 mb-2">
                    <img
                        src={blog.doctorImage}
                        alt={blog.doctorName}
                        className="rounded-circle shadow"
                        style={{ width: 40, height: 40, objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "/assets/img/defaultDoctorProfile.jpg";
                        }}
                      />
                    <div className="ms-3">
                      <h6 className="mb-0 ml-2">{blog.doctorName}</h6>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-auto pt-2 border-top">
                    <small className="text-muted">Published {formatDate(blog.createdAt)}</small>
                    <Button variant="outline-info rounded-pill btn-sm" size="sm" onClick={() => onSelectBlog(blog)}>
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DoctorsBlogPage;
